import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { WA_NUMBER } from './SiteChrome'
import { whatsAppCtaProps } from '../lib/analytics'
import { toExternalImage } from '../lib/dropify'

const PROOF_IMAGE_TRANSFORM = { width: 420, height: 560, smart: true, format: 'webp', quality: 58 } as const

export type TrustQuestion = {
  q: string
  a: string
}

// Kept as a fixed constant so external tests can assert FAQ + trust question
// coverage without depending on the removed generic fallback generator.
export const BRAND_TRUST_QUESTION_COUNT = 5

export function BrandTrustSections({
  brandName,
  itemName,
  images,
  questions,
}: {
  brandName: string
  itemName: string
  images: string[]
  questions: TrustQuestion[]
}) {
  const message = `Halo Dropify, saya ingin menanyakan ${itemName}. Kebutuhan: ___. Kota tujuan: ___. Mohon periksa stok dan siapkan quotation.`

  return (
    <>
      {images.length > 0 && (
        <section className="border-y border-slate-100 bg-slate-50 py-14 md:py-16" aria-labelledby={`bukti-${brandName.replace(/\s+/g, '-').toLowerCase()}`}>
          <div className="mx-auto max-w-6xl px-4">
            <div className="max-w-2xl">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-green-700">Pesanan pelanggan Dropify</p>
              <h2 id={`bukti-${brandName.replace(/\s+/g, '-').toLowerCase()}`} className="mt-3 text-2xl font-black tracking-tight text-slate-950 md:text-3xl">
                Pesanan grosir yang sudah diproses Dropify
              </h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Dropify melayani toko, reseller, pabrik, dan tim pengadaan di berbagai kota di Indonesia.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {images.slice(0, 8).map((src, index) => (
                <figure key={`${src}-${index}`} className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ${index >= 6 ? 'hidden lg:block' : ''}`}>
                  <img
                    src={toExternalImage(src, PROOF_IMAGE_TRANSFORM)}
                    alt="Dokumentasi pesanan pelanggan Dropify"
                    width="420"
                    height="560"
                    loading="lazy"
                    decoding="async"
                    className="aspect-[3/4] w-full object-cover"
                  />
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-white py-14 md:py-16" aria-labelledby={`pertanyaan-${brandName.replace(/\s+/g, '-').toLowerCase()}`}>
        <div className="mx-auto max-w-5xl px-4">
          <div className="max-w-3xl">
            <h2 id={`pertanyaan-${brandName.replace(/\s+/g, '-').toLowerCase()}`} className="text-2xl font-black tracking-tight text-slate-950 md:text-3xl">
              Pertanyaan umum sebelum memesan
            </h2>
            <p className="mt-3 text-base leading-relaxed text-slate-600 md:text-lg">
              Jawaban berikut menjelaskan stok, sample, susunan varian, pengiriman, dan data yang diperlukan untuk quotation.
            </p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {questions.map((item) => (
              <article key={item.q} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <h3 className="font-black leading-snug text-slate-950">{item.q}</h3>
                <div className="mt-3 flex gap-3">
                  <FontAwesomeIcon icon={faCheck} className="mt-1 h-4 w-4 flex-shrink-0 text-green-700" />
                  <p className="text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                </div>
              </article>
            ))}
          </div>

          <p className="mt-7 max-w-3xl border-l-2 border-blue-500 pl-4 text-sm leading-relaxed text-slate-700">
            Untuk pesanan pertama, Anda dapat memilih ekspedisi yang dipercaya dan memeriksa kiriman sebelum transfer.{' '}
            <a href="/jaminan-ekspedisi" className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900">Pelajari alur verifikasi lewat ekspedisi</a>.
          </p>

          <a
            href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`}
            target="_blank"
            rel="noopener noreferrer"
            {...whatsAppCtaProps('landing_page', { cta_placement: 'brand_trust_questions', lp_category: brandName, lead_item_name: itemName })}
            className="mt-7 inline-flex items-center gap-2 rounded-xl bg-green-700 px-6 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
            Periksa stok {brandName}
            <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>
    </>
  )
}
